import { Component } from '@angular/core';
import { CartridgeRouteBuilderService } from '@markant-platform/cartridge/data-access';
import { filter, first, map, ReplaySubject, Subject, switchMap, tap } from 'rxjs';
import {
  ActivatedRoute,
  NavigationEnd,
  Route,
  Router,
  RouterLink,
  RouterOutlet,
  Scroll,
  UrlSegment,
  UrlSegmentGroup,
} from '@angular/router';
import { MarkantElementsModule } from '@markant/elements-angular';
import { NgIf } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'markant-platform-focus-view',
  templateUrl: './focus-view.component.html',
  styleUrls: ['./focus-view.component.scss'],
  imports: [MarkantElementsModule, RouterOutlet, RouterLink, NgIf, TranslocoModule],
  standalone: true,
})
export class FocusViewComponent {
  error = false;

  private navigationInitialized: Subject<boolean> = new ReplaySubject(1);

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private routeBuilder: CartridgeRouteBuilderService
  ) {
    this.initCartridgeRouting();
  }

  private initCartridgeRouting(): void {
    const activatedRouteConfig: Route | null = this.activatedRoute.routeConfig;

    // routeConfig can be null
    if (activatedRouteConfig) {
      if (activatedRouteConfig.children) {
        this.navigationInitialized.next(true);
        return;
      }
      this.router.events
        .pipe(
          // extract navigation end event from scroll event emitted by the router
          map(event => (event instanceof Scroll ? event.routerEvent : event)),
          // wait for navigation end event
          filter(event => event instanceof NavigationEnd),
          // stop listening after receiving event
          first(),
          // get original url to navigate to
          map(event => (event as NavigationEnd).url),
          // generate remote routes from booked cartridges
          switchMap((url: string) => this.routeBuilder.buildRemoteRoutes().pipe(map(routes => ({ url, routes })))),
          // update children definition of activted route and navigate to original destination
          tap(({ url, routes }) => {
            activatedRouteConfig.children = routes;

            // Only route to sub url when route is registered, otherwise navigate to route
            // Emulates the execution of the route's registered matcher to check if the url matches the route
            if (routes.some(route => this.hasMatchingURL(route, url))) {
              this.router.navigateByUrl(url, { replaceUrl: true }).then(() => this.navigationInitialized.next(true));
            } else {
              this.router.navigateByUrl('/', { replaceUrl: true }).then(() => this.navigationInitialized.next(true));
            }
          })
        )
        .subscribe();
    }
  }

  private hasMatchingURL(route: Route, url: string): boolean {
    if (route.matcher) {
      const data = url
        .split('/')
        .filter(urlSegment => !!urlSegment && urlSegment !== 'focus-view')
        .map(urlSegment => new UrlSegment(urlSegment, {}));

      return !!route.matcher(data, undefined as unknown as UrlSegmentGroup, undefined as unknown as Route);
    }

    if (route.path) {
      return url.split('?')[0].replace('/focus-view/', '').startsWith(route.path);
    }

    return false;
  }
}
